import React from 'react';
import { path } from 'ramda';
import PropTypes from 'prop-types';
import Decimal from 'decimal.js-light';
import { useMutation, useQuery } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_CANCEL_HOTEL_REFUND_PROPOSAL, GET_HOTEL_REFUND_PROPOSAL, HOTEL_BOOKING, GET_HOTEL_BOOKINGS_INFO } from 'store/queries';
import { CANCEL_HOTEL_PRODUCT } from 'store/mutations';
import { getBookingJourneysURL } from 'utils/urls';
import { ACTION_CODES } from 'constants/errors';
import { hotelsShape } from 'constants/propTypes';
import { buildRefundProposal, buildHotelBookingPenalties, buildRefundProposalFromBookingPenalty } from 'utils/hotel';

const subtractRefundAmountFromOriginalPrice = (amount = 0, refundPropsal = 0) => new Decimal(amount).minus(refundPropsal).toNumber();

const HotelRefundWrapper = ({ booking, hotels, children }) => {
  const isDotwBooking = booking.hotels[0]?.isDotwBooking;

  const hotelsDataState = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKINGS_INFO, {
    variables: isDotwBooking ? { reference: booking.reference } : { bookingReference: booking.reference }
  });

  const isAlreadyCancelled = hotelsDataState.data?.hotelBooking?.rooms?.every(({ status }) => status === 'CANCELED') ?? true;

  const hotelRefundState = useQuery(isDotwBooking ? GET_CANCEL_HOTEL_REFUND_PROPOSAL : GET_HOTEL_REFUND_PROPOSAL, {
    variables: isDotwBooking ? { reference: booking.reference } : { bookingReference: booking.reference },
    skip: isAlreadyCancelled
  });

  const [cancelHotelProduct, cancelHotelState] = useMutation(CANCEL_HOTEL_PRODUCT, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true,
    skip: hotelRefundState.loading
  });

  const state = [hotelRefundState, cancelHotelState];
  const error = state.map((item) => item.error).find(Boolean);
  const loading = state.some((item) => item.loading);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getBookingJourneysURL(booking.reference)
    });
  }

  if (loading) {
    return <Spinner />;
  }

  if (!hotelRefundState.loading && !cancelHotelState.called && !hotelsDataState.loading) {
    let refundProposalData = null;

    if (!isDotwBooking) {
      refundProposalData = hotelRefundState.data.refundProposal;
    } else if (isDotwBooking && isAlreadyCancelled) {
      const penaltiesApplied = buildHotelBookingPenalties(hotelsDataState.data);
      refundProposalData = buildRefundProposalFromBookingPenalty(penaltiesApplied, hotels);
    } else if (isDotwBooking) {
      refundProposalData = buildRefundProposal(hotelRefundState.data);
    }

    const proposedRefundAmount = path(['breakdown', isDotwBooking ? 'refund' : 'hotel', 'amount'], refundProposalData) ?? 0;
    const proposedRefundMarginAmount = path(['breakdown', 'hotelMargin', 'amount'], refundProposalData) ?? 0;
    const proposedRefundFeeAmount = path(['breakdown', 'hotel', 'amount'], refundProposalData) ?? hotels[0].price;

    hotels.forEach((hotel) => {
      const hotelFee = subtractRefundAmountFromOriginalPrice(hotel.price, proposedRefundAmount);
      const hotelMarginFee = subtractRefundAmountFromOriginalPrice(hotel.margin, proposedRefundMarginAmount);

      if (!hotel.cancelled) {
        cancelHotelProduct({
          variables: {
            bookingReference: booking.reference,
            fee: isDotwBooking ? proposedRefundFeeAmount : hotelFee,
            marginFee: isDotwBooking ? proposedRefundMarginAmount : hotelMarginFee,
            itemRef: hotel.itemRef,
            skipConfirmation: true
          }
        });
      }
    });
  }

  return children;
};

HotelRefundWrapper.propTypes = {
  booking: PropTypes.object,
  children: PropTypes.element,
  hotels: hotelsShape
};

export default HotelRefundWrapper;
